import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useTransactions, useTransactionCategories } from "@/hooks/use-transactions";

// Define the transaction schema
const transactionSchema = z.object({
  date: z.string().min(1, "Date is required"),
  description: z.string().min(1, "Description is required"),
  amount: z.coerce.number().positive("Amount must be greater than 0"),
  type: z.enum(["income", "expense"], {
    required_error: "Transaction type is required",
  }),
  categoryId: z.union([
    z.literal("auto"),    // Special value for auto-categorization
    z.string().regex(/^\d+$/).transform(Number), // Numeric string that will be transformed to a number
    z.number(),           // Already a number
    z.null(),             // Allow null
  ]).optional(),
});

type TransactionFormValues = z.infer<typeof transactionSchema>;

interface AddTransactionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddTransactionForm({
  open,
  onOpenChange,
  onSuccess,
}: AddTransactionFormProps) {
  const { createTransaction, isPendingCreate } = useTransactions();
  const { categories } = useTransactionCategories();
  const { toast } = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  
  // Set default date to today
  const today = format(new Date(), "yyyy-MM-dd");
  
  // Initialize form with default values
  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      date: today,
      description: "",
      amount: 0,
      type: "expense",
      categoryId: "auto",
    },
  });
  
  // Get the currently selected transaction type
  const transactionType = form.watch("type");
  
  // Filter categories by transaction type
  const filteredCategories = categories;
  
  const onSubmit = async (data: TransactionFormValues) => {
    try {
      setFormError(null);
      
      console.log("Form data submitted:", data);
      
      // Format the date correctly for the server
      const dateObj = new Date(data.date);
      // Ensure proper ISO format for the server
      const formattedDate = dateObj.toISOString();
      
      // Construct the transaction data
      // NOTE: Not passing the date as a full Date object but as a string that the server will parse
      const transactionData = {
        date: data.date, // Send as ISO string - server will handle the conversion
        description: data.description,
        amount: data.amount.toString(),
        type: data.type,
        categoryId: data.categoryId === "auto" ? undefined : 
                   (typeof data.categoryId === "string" ? 
                   parseInt(data.categoryId) : data.categoryId),
        status: "pending"
      };
      
      console.log("Transaction data to be sent:", transactionData);
      
      // Create the transaction using the useTransactions hook
      try {
        // Use the hook's createTransaction method
        const response = await createTransaction(transactionData);
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error("Server error response:", errorData);
          
          // Handle trial limit errors specifically
          if (errorData.error === "TRIAL_LIMIT_REACHED") {
            toast({
              title: "Trial Limit Reached",
              description: errorData.message || "You've reached your trial limit for AI categorizations. Please upgrade your plan to continue.",
              variant: "destructive",
            });
            // Close the dialog even on error to avoid confusing the user
            onOpenChange(false);
            return;
          } else if (errorData.error === "PARTIAL_TRIAL_LIMIT") {
            // Show warning but proceed with transaction creation
            toast({
              title: "Trial Limit Warning",
              description: errorData.message || `You can only categorize ${errorData.remaining} more transactions with your trial.`,
              variant: "destructive",
            });
          }
          
          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log("Transaction created successfully:", result);
        
        // Reset the form
        form.reset({
          date: today,
          description: "",
          amount: 0,
          type: "expense",
          categoryId: "auto",
        });
        
        // Show success message
        let description = "The transaction was created successfully.";
        
        // Add AI categorization message if applicable
        if (data.categoryId === "auto" && result.aiCategorized) {
          description = "The transaction was created successfully and automatically categorized using AI.";
          
          // If trial usage information is provided, show it
          if (result.trialUsage) {
            const { used, limit, remaining } = result.trialUsage;
            toast({
              title: "Trial Usage Updated",
              description: `You've used ${used}/${limit} AI categorizations (${remaining} remaining).`,
              variant: "default",
            });
          }
        }
        
        toast({
          title: "Transaction created",
          description: description,
        });
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        // Close the dialog
        onOpenChange(false);
      } catch (fetchError) {
        console.error("Fetch error:", fetchError);
        throw fetchError;
      }
    } catch (error) {
      console.error("Failed to create transaction:", error);
      setFormError("Failed to create transaction. Please check the form and try again.");
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create transaction. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Transaction</DialogTitle>
          <DialogDescription>
            Enter transaction details. It will be automatically categorized using AI.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {formError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {formError}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Office supplies" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="amount"
              render={({ field: { value, onChange, ...restField } }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      value={value === 0 ? "" : value}
                      onChange={(e) => {
                        const newValue = e.target.value === "" ? 0 : parseFloat(e.target.value);
                        onChange(newValue);
                      }}
                      {...restField}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value == null ? undefined : String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Leave empty for AI categorization" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="auto">Auto-categorize with AI</SelectItem>
                      {filteredCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPendingCreate}>
                {isPendingCreate ? "Creating..." : "Create Transaction"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
